import { useEffect, useState, useRef, useCallback } from "react";
import { FileStack, RefreshCw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";

// 合同订单产品一览表 — one-way FileMaker (new09101 / data_api) → App pull.
// Nightly cron 02:30 America/New_York + this manual trigger (admin only).
export function ContractOrderSyncCard() {
  const { t } = useLanguage();
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [since, setSince] = useState("");
  const timer = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/contract-orders/sync/status");
      setStatus(data);
      setSince((prev) => prev || data.since || "");
      if (data.state !== "running" && timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    } catch (error) {
      if (![401, 403].includes(error?.response?.status)) {
        console.warn("Contract-orders sync status fetch failed:", error);
      }
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [fetchStatus]);

  useEffect(() => {
    if (status?.state === "running" && !timer.current) {
      timer.current = setInterval(fetchStatus, 3000);
    }
  }, [status, fetchStatus]);

  const startSync = async () => {
    setStarting(true);
    try {
      await api.post("/contract-orders/sync", since ? { since } : {});
      toast.success(t("fm.started"));
      fetchStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setStarting(false);
    }
  };

  const running = status?.state === "running";
  const pct = status?.total ? Math.round((status.processed / status.total) * 100) : 0;

  return (
    <div className="bg-card border border-border rounded-md p-6 space-y-4 mb-5" data-testid="co-sync-card">
      <h3 className="font-heading text-base font-bold tracking-tight flex items-center gap-2">
        <FileStack size={16} className="text-brand" /> {t("co.syncTitle")}
      </h3>
      <p className="text-xs text-muted-foreground -mt-2">{t("co.syncHint")}</p>

      {/* 2026-06 · 起始日期存数据库 → 生产环境可直接改，不用重新部署 */}
      <div className="space-y-1 max-w-[220px]">
        <Label className="text-[11px] text-muted-foreground">{t("co.sinceLabel")}</Label>
        <Input value={since} onChange={(e) => setSince(e.target.value)} placeholder="01/01/2020"
          className="h-8 font-mono" data-testid="co-since-input" />
      </div>

      {status && (
        <p className="text-xs font-mono text-muted-foreground" data-testid="co-sync-status">
          {running
            ? `${t("co.syncing")} ${status.processed}/${status.total} (${pct}%)`
            : status.state === "error"
              ? `${t("fm.failed")}: ${status.error || ""}`
              : t("co.syncCount").replace("{n}", status.record_count ?? 0)}
        </p>
      )}

      <Button onClick={startSync} disabled={running || starting} data-testid="co-sync-button"
        variant="outline" className="rounded-md">
        <RefreshCw size={14} className={running ? "animate-spin" : ""} />
        {running ? t("co.syncing") : t("co.syncNow")}
      </Button>
    </div>
  );
}
